<script setup lang="ts">
import { computed, ref, type CSSProperties } from 'vue';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import { InputType } from '@/interfaces/InputFields';
import { useScroll } from '@vueuse/core';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import ConnxUsageCard from '@/components/Molecules/ConnxUsageCard.vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import ConnxModal from '@/components/Molecules/ConnxModal.vue';
import { Widget } from '@/interfaces/Dashboard';
import { getWidgetVisualIcon } from '@/lib/chart-helpers';

const dashboardStore = useDashboardStore();
const currentHelpWidget = ref<Widget | null>(null);
const { y: documentYScrollPosition } = useScroll(document);
const widgetCategories = ['Widget', 'Insight', 'AI'];

const sidebarStyles = computed<CSSProperties>(() => {
  return documentYScrollPosition.value >= 200
    ? { position: 'fixed', top: '4rem', right: '8px', width: '312px' }
    : { top: '0px' };
});

const changeCategoryFilter = (category: string) => {
  if (category === 'All' || dashboardStore.widgetCategoryFilter === category) {
    return (dashboardStore.widgetCategoryFilter = '');
  }
  dashboardStore.widgetCategoryFilter = category;
};

const dashboardWidgetListStyles = computed<CSSProperties>(() => {
  const height = `calc(100vh - ${documentYScrollPosition.value >= 200 ? '128px' : '200px'})`;

  return {
    height: height,
  };
});

const emit = defineEmits(['widget-drag-start', 'widget-drag', 'widget-drag-end', 'select-widget']);
</script>
<template>
  <div class="tw-min-w-96">
    <div
      class="tw-border-l tw-border-b tw-border-solid tw-p-4 tw-transition-all tw-duration-300 tw-bg-connx-white"
      :style="sidebarStyles"
    >
      <div class="DashboardFilters tw-relative">
        <connx-usage-card
          v-if="(dashboardStore.activeDashboardTab?.dashboardTabWidgets ?? []).length === 0"
          :messages="[
            {
              iconPositionCss: 'tw-top-1',
              title: 'Filter by Name',
              description: 'Use the search bar at the top to type a widget\'s name and quickly find it.',
            },
            {
              iconPositionCss: 'tw-top-0',
              title: 'Add a Widget',
              description:
                'When you find the widget you want, just click and drag it to add it to the main panel which you can then drag and position however and wherever you like.',
            },
          ]"
        ></connx-usage-card>

        <connx-field
          v-model="dashboardStore.widgetFilter"
          :type="InputType.Text"
          :maxlength="20"
          name="widget-type"
          placeholder="Search"
          class="tw-mb-2"
        >
        </connx-field>
        <ul class="tw-gap-x-2 tw-mt-4 tw-mb-2 tw-hidden">
          <li class="tw-hidden">
            <connx-button
              :button-style="dashboardStore.widgetCategoryFilter === '' ? 'secondary' : 'white'"
              button-size="sm"
              @click="changeCategoryFilter('All')"
            >
              All
            </connx-button>
          </li>
          <li v-for="category in widgetCategories" :key="category">
            <connx-button
              :button-style="dashboardStore.widgetCategoryFilter === category ? 'secondary' : 'white'"
              button-size="sm"
              @click="changeCategoryFilter(category)"
            >
              {{ category }}
            </connx-button>
          </li>
        </ul>
      </div>
      <div class="ScrollBar ScrollBar--vertical">
        <div :style="dashboardWidgetListStyles">
          <div v-if="dashboardStore.filteredWidgetSelection.length === 0" class="tw-pt-4">
            <h6 v-if="dashboardStore.widgets.length === 0">No widgets available to select from.</h6>
            <h6 v-else>No widgets match the current filters</h6>
          </div>
          <ul>
            <li v-for="widget in dashboardStore.filteredWidgetSelection" :key="widget.label" class="tw-mr-2">
              <div
                type="button"
                draggable="true"
                unselectable="on"
                @dragstart="(payload: DragEvent) => emit('widget-drag-start', payload)"
                @drag="(payload: DragEvent) => emit('widget-drag', payload)"
                @dragend="(payload: DragEvent) => emit('widget-drag-end', payload)"
                @mousedown="() => emit('select-widget', widget)"
                class="tw-relative tw-max-w-96 tw-w-full tw-group tw-my-4 tw-rounded-md tw-px-8 tw-py-5 tw-bg-connx-white tw-shadow-lg tw-border tw-border-solid tw-border-connx-grey-tint-80"
              >
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-x-4 tw-break-word">
                  <h5>{{ widget.label }}</h5>
                  <connx-button
                    title="Help"
                    v-if="widget.helpFieldText"
                    button-style="blank"
                    class="tw-absolute tw-top-0 tw-right-0 tw-flex-shrink-0 !tw-rounded-lg tw-opacity-0 group-hover:tw-opacity-100 tw-text-connx-grey-shade-20"
                    @click="() => (currentHelpWidget = widget)"
                  >
                    <connx-icon
                      class="tw-h-5 tw-flex-shrink-0"
                      name="far fa-question-circle"
                      title="Help Text"
                    ></connx-icon>
                  </connx-button>
                </div>
                <div class="tw-flex tw-gap-x-4">
                  <div
                    class="tw-w-12 tw-h-12 tw-border-solid tw-border tw-rounded-md tw-p-2 tw-flex tw-items-center tw-justify-center tw-bg-connx-grey-tint-95 svg-full tw-flex-shrink-0"
                  >
                    <connx-icon
                      class="tw-h-7 tw-w-auto tw-text-connx-primary"
                      :name="getWidgetVisualIcon(widget).icon"
                      :class="getWidgetVisualIcon(widget).class"
                    />
                  </div>
                  <div v-html="widget.description" class="tw-break-word"> </div>
                </div>
                <div class="tw-mt-4 tw-hidden">
                  <p class="tw-mb-0"
                    ><strong>#{{ widget.category }}</strong></p
                  >
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  <connx-modal
    scrollable
    type="side"
    :title="currentHelpWidget?.label"
    :open="currentHelpWidget !== null"
    @close="currentHelpWidget = null"
  >
    <div v-html="currentHelpWidget?.helpFieldText"></div>
    <template #footer>
      <div class="tw-flex tw-items-center tw-justify-center">
        <connx-button button-style="secondary" @click="currentHelpWidget = null"> Close </connx-button>
      </div>
    </template>
  </connx-modal>
</template>
